import type {
  AsyncAcceptedResponse,
  UploadPartRequest,
  UploadSession,
} from "@webyonetim/contracts";

import { IncrementalSha256 } from "../../lib/sha256";

export const BROWSER_UPLOAD_CHUNK_BYTES = 8 * 1_024 * 1_024;

export interface UploadTransport {
  readonly append: (
    uploadSessionId: string,
    metadata: UploadPartRequest,
    bytes: Blob,
    signal?: AbortSignal,
  ) => Promise<UploadSession>;
  readonly complete: (
    uploadSessionId: string,
    digest: string,
    expectedRevision: number,
  ) => Promise<AsyncAcceptedResponse>;
}

export interface UploadProgress {
  readonly phase: "rehashing" | "uploading" | "finalizing";
  readonly processedBytes: number;
  readonly totalBytes: number;
}

export interface UploadFileResult {
  readonly accepted: AsyncAcceptedResponse;
  readonly digest: `sha256:${string}`;
  readonly session: UploadSession;
}

/**
 * Resumes an upload from the server-authoritative byte offset. The full archive
 * hash is rebuilt by rereading the local File in bounded chunks; the browser
 * never retains the complete archive in memory.
 */
export async function uploadFileInParts(input: {
  readonly file: Blob;
  readonly session: UploadSession;
  readonly transport: UploadTransport;
  readonly signal?: AbortSignal;
  readonly onProgress?: (progress: UploadProgress) => void;
}): Promise<UploadFileResult> {
  const { file, signal, transport } = input;
  let session = input.session;
  assertUploadCanContinue(file, session);
  const chunkBytes = Math.min(session.partSize, BROWSER_UPLOAD_CHUNK_BYTES);
  const fullDigest = new IncrementalSha256();

  let offset = 0;
  while (offset < session.receivedBytes) {
    throwIfAborted(signal);
    const end = Math.min(session.receivedBytes, offset + chunkBytes);
    fullDigest.update(await readBytes(file.slice(offset, end)));
    offset = end;
    input.onProgress?.({ phase: "rehashing", processedBytes: offset, totalBytes: file.size });
  }

  while (offset < file.size) {
    throwIfAborted(signal);
    const end = Math.min(file.size, offset + chunkBytes);
    const part = file.slice(offset, end);
    const bytes = await readBytes(part);
    const partDigest = await sha256(bytes);
    fullDigest.update(bytes);
    const metadata: UploadPartRequest = {
      partNumber: Math.floor(offset / chunkBytes),
      offset,
      contentLength: bytes.byteLength,
      digest: partDigest,
      expectedRevision: session.revision,
    };
    session = await transport.append(session.id, metadata, part, signal);
    if (session.receivedBytes !== end || session.contentLength !== file.size) {
      throw new Error("Sunucu upload ilerlemesi yerel dosyayla eşleşmiyor.");
    }
    offset = end;
    input.onProgress?.({ phase: "uploading", processedBytes: offset, totalBytes: file.size });
  }

  throwIfAborted(signal);
  const digest = fullDigest.digestHex();
  input.onProgress?.({ phase: "finalizing", processedBytes: file.size, totalBytes: file.size });
  const accepted = await transport.complete(session.id, digest, session.revision);
  return { accepted, digest, session };
}

function assertUploadCanContinue(file: Blob, session: UploadSession): void {
  if (session.status !== "open") throw new Error("Bu upload oturumu artık veri kabul etmiyor.");
  if (file.size !== session.contentLength) {
    throw new Error("Seçilen dosya, upload oturumunu başlatan dosyayla aynı boyutta değil.");
  }
  if (
    !Number.isSafeInteger(session.receivedBytes) ||
    session.receivedBytes < 0 ||
    session.receivedBytes > file.size ||
    !Number.isSafeInteger(session.partSize) ||
    session.partSize < 1
  ) {
    throw new Error("Sunucu geçersiz bir upload ilerlemesi döndürdü.");
  }
}

async function readBytes(blob: Blob): Promise<Uint8Array<ArrayBuffer>> {
  return new Uint8Array(await blob.arrayBuffer());
}

async function sha256(bytes: Uint8Array<ArrayBuffer>): Promise<`sha256:${string}`> {
  const digest = new Uint8Array(await crypto.subtle.digest("SHA-256", bytes));
  return `sha256:${Array.from(digest, (byte) => byte.toString(16).padStart(2, "0")).join("")}`;
}

function throwIfAborted(signal: AbortSignal | undefined): void {
  if (signal?.aborted !== true) return;
  if (signal.reason instanceof Error) throw signal.reason;
  throw new DOMException("Upload duraklatıldı.", "AbortError");
}
